/**
 * THE FX GATE PANEL — v19's one hard rule, as a SCREEN STATE (not an error, not an empty).
 *
 * When any exchange rate the group view needs is a placeholder, the server answers
 * `fx.status = 'unverified'` and `consolidated = null`. This panel is what the reader sees
 * in that state. It is designed so it CANNOT be mistaken for data:
 *   · it renders NO figure — no zero, no greyed number, no "approx.", no tile;
 *   · it NAMES every unverified pair, its provenance (seeded placeholder / flagged /
 *     missing), the book period consulted, and how old the placeholder is;
 *   · it NAMES who can clear it and how — `fx.cleared_by` is served by the API
 *     (`config.global` via `PUT /fx-rates/:period`), so nobody has to guess;
 *   · the export control for INR figures does not exist in this state — the page renders
 *     it only from a `consolidated` object, and there is none (rule 14: an export is a
 *     channel).
 * The panel never shows a RATE VALUE: `dashboard.group` is not `config.global`, and the
 * route does not carry one (proposal §8 #3).
 *
 * Tone: the status/warning pair (measured 4.51:1, mode-invariant) on the heading strip
 * only; the body is `text-fg` on `bg-surface` so the table is as legible as any register.
 */

import type { FxGateDto, FxPairStatus } from './api';
import { formatDateLong } from '../inquiries/format';

function provenance(pair: FxPairStatus | undefined): string {
  if (pair === undefined || pair.missing) return 'No rate on file';
  if (pair.source === 'seed') return 'Seeded placeholder — verify before use';
  return 'Flagged verify-before-prod';
}

export function FxGatePanel({ fx }: { fx: FxGateDto }): React.JSX.Element {
  const byCcy = new Map(fx.pairs.map((pair) => [pair.from_ccy, pair] as const));
  return (
    <section
      role="status"
      aria-live="polite"
      aria-labelledby="fx-gate-title"
      data-testid="fx-gate"
      className="flex flex-col gap-4 rounded-md border-2 border-warn-fg bg-surface p-5"
    >
      <header className="flex flex-col gap-2">
        <span className="inline-flex w-fit items-center rounded-pill bg-warn-bg px-2.5 py-1 text-label font-medium text-warn-fg">
          Exchange rates unverified
        </span>
        <h2 id="fx-gate-title" className="text-h3 text-fg">
          No consolidated figure is shown until every rate below is verified
        </h2>
        <p className="text-body13 text-fg-secondary">
          The group total in INR is withheld — not zero, not approximate, withheld. The
          per-country figures below this panel are in each country&apos;s own currency and
          never touched a rate. Book period consulted:{' '}
          <span className="tnum font-medium text-fg">{fx.period}</span>.
        </p>
      </header>

      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <caption className="sr-only">The exchange-rate pairs the group view cannot use yet</caption>
          <thead>
            <tr className="h-10 border-b border-line bg-subtle">
              <th scope="col" className="px-3 text-th uppercase text-fg">Currency → INR</th>
              <th scope="col" className="px-3 text-th uppercase text-fg">Provenance</th>
              <th scope="col" className="px-3 text-th uppercase text-fg">Book period used</th>
              <th scope="col" className="px-3 text-th uppercase text-fg">Placeholder since</th>
            </tr>
          </thead>
          <tbody>
            {fx.unverified.map((ccy) => {
              const pair = byCcy.get(ccy);
              return (
                <tr key={ccy} data-ccy={ccy} className="border-b border-line last:border-b-0">
                  <td className="px-3 py-2 align-middle font-mono text-mono text-fg">{ccy}</td>
                  <td className="px-3 py-2 align-middle text-body13 text-fg">
                    {provenance(pair)}
                    {pair?.stale === true ? ' · carried from an earlier period' : ''}
                  </td>
                  <td className="px-3 py-2 align-middle tnum text-body13 text-fg">
                    {pair?.period_used ?? '—'}
                  </td>
                  <td className="px-3 py-2 align-middle text-body13 text-fg">
                    {pair?.since === null || pair?.since === undefined ? '—' : formatDateLong(pair.since)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-body13 text-fg" data-testid="fx-gate-cleared-by">
        <span className="font-medium">Who clears this:</span> a group administrator, or any
        holder of <code className="font-mono text-mono">{fx.cleared_by.permission}</code>, via{' '}
        <code className="font-mono text-mono">{fx.cleared_by.route}</code>. {fx.cleared_by.how}
      </p>
    </section>
  );
}
